import { Select as SelectPrimitive } from '@base-ui/react/select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'

export interface SelectOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

export interface SelectProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'defaultValue' | 'name' | 'onChange' | 'value'> {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  /** Compatibility bridge for replacing a native select without changing form logic. */
  onChange?: React.ChangeEventHandler<HTMLSelectElement>
  options?: readonly SelectOption[]
  children?: React.ReactNode
  placeholder?: string
  name?: string
  form?: string
  required?: boolean
  autoComplete?: string
}

function textOf(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textOf(node.props.children)
  return ''
}

function optionsFromChildren(children: React.ReactNode): SelectOption[] {
  const options: SelectOption[] = []
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (child.type === React.Fragment) {
      options.push(...optionsFromChildren((child.props as { children?: React.ReactNode }).children))
      return
    }
    if (child.type !== 'option') return
    const props = child.props as React.OptionHTMLAttributes<HTMLOptionElement>
    options.push({
      value: String(props.value ?? textOf(props.children)),
      label: textOf(props.children),
      description: props.title,
      disabled: props.disabled,
    })
  })
  return options
}

/** Base UI select for short enum lists. Use Picker when the choices need search. */
export function Select({
  value,
  defaultValue,
  onValueChange,
  onChange,
  options: optionsProp,
  children,
  placeholder = 'Choose an option',
  name,
  form,
  required,
  autoComplete,
  id,
  disabled,
  className,
  ...triggerProps
}: SelectProps) {
  const childOptions = React.useMemo(() => optionsFromChildren(children), [children])
  const options = optionsProp ?? childOptions
  const items = React.useMemo(() => options.map((option) => ({ value: option.value, label: option.label })), [options])

  const change = (next: string | null) => {
    const nextValue = next ?? ''
    onValueChange?.(nextValue)
    if (onChange) {
      const target = { value: nextValue, name: name ?? '' } as HTMLSelectElement
      onChange({ target, currentTarget: target } as React.ChangeEvent<HTMLSelectElement>)
    }
  }

  return (
    <SelectPrimitive.Root
      items={items}
      value={value}
      defaultValue={defaultValue}
      onValueChange={change}
      name={name}
      form={form}
      required={required}
      autoComplete={autoComplete}
      id={id}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        type="button"
        disabled={disabled}
        data-slot="select-trigger"
        className={cn(
          'h-8 min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-card px-2.5 text-sm text-foreground outline-none transition-colors',
          'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25',
          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 aria-invalid:border-destructive',
          className,
          // Native select callers may bring `block`; the trigger still needs flex layout for its value and icon.
          'inline-flex',
        )}
        {...triggerProps}
      >
        <SelectPrimitive.Value
          className="min-w-0 flex-1 truncate text-left data-[placeholder]:text-muted-foreground"
          placeholder={placeholder}
        />
        <SelectPrimitive.Icon className="shrink-0 text-muted-foreground">
          <ChevronDown aria-hidden className="size-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Positioner align="start" alignItemWithTrigger={false} sideOffset={4} className="z-50 outline-none">
          <SelectPrimitive.Popup
            data-slot="select-popup"
            className={cn(
              'max-h-[min(20rem,var(--available-height))] min-w-[var(--anchor-width)] max-w-[min(24rem,calc(100vw-1rem))] origin-[var(--transform-origin)] overflow-hidden',
              'rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg outline-none',
              'transition-[opacity,transform] duration-(--duration-fast) data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
            )}
          >
            <SelectPrimitive.ScrollUpArrow className="sticky top-0 z-10 flex h-5 items-center justify-center bg-popover text-muted-foreground">
              <ChevronUp aria-hidden className="size-3.5" />
            </SelectPrimitive.ScrollUpArrow>
            <SelectPrimitive.List className="max-h-[min(19rem,var(--available-height))] overflow-y-auto scroll-py-1">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  data-value={option.value}
                  label={option.label}
                  disabled={option.disabled}
                  className={cn(
                    'grid min-h-8 cursor-default grid-cols-[1rem_minmax(0,1fr)] items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none',
                    'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50 max-pane:min-h-11',
                  )}
                >
                  <SelectPrimitive.ItemIndicator className="col-start-1 text-primary">
                    <Check aria-hidden className="size-4" />
                  </SelectPrimitive.ItemIndicator>
                  <span className="col-start-2 min-w-0">
                    <SelectPrimitive.ItemText className="block truncate">{option.label}</SelectPrimitive.ItemText>
                    {option.description ? <span className="block truncate text-xs text-muted-foreground">{option.description}</span> : null}
                  </span>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.List>
            <SelectPrimitive.ScrollDownArrow className="sticky bottom-0 z-10 flex h-5 items-center justify-center bg-popover text-muted-foreground">
              <ChevronDown aria-hidden className="size-3.5" />
            </SelectPrimitive.ScrollDownArrow>
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
